import { IsInt, IsPositive } from 'class-validator';

/**
 * Stable `code` of every `switch-to-annual` refusal, so the dialog reacts to
 * the cause (refresh the amount, complete 3DS on Stripe, fix the card) instead
 * of the HTTP status.
 */
export const SWITCH_TO_ANNUAL_ERROR_CODE = {
  alreadyAnnual: 'switch_to_annual_already_annual',
  notAllowed: 'switch_to_annual_not_allowed',
  previewExpired: 'switch_to_annual_preview_expired',
  paymentDeclined: 'switch_to_annual_payment_declined',
  paymentNeedsAuthentication: 'switch_to_annual_payment_needs_authentication',
  /** Stripe certainly did not switch: a retry may work. */
  failed: 'switch_to_annual_failed',
  /** The switch may have gone through: nothing is claimed, no immediate retry. */
  outcomeUnknown: 'switch_to_annual_outcome_unknown',
} as const;

/**
 * Confirms the monthly → annual switch previewed by
 * `GET /subscription/switch-to-annual/preview`. The `prorationDate` echoed back
 * from the preview makes Stripe compute the unused-time credit at the same
 * instant, so the amount charged equals the amount shown.
 */
export class SwitchToAnnualDto {
  /** Unix epoch seconds, as returned by the preview. */
  @IsInt()
  @IsPositive()
  prorationDate!: number;
}

/**
 * Exact amounts of the monthly → annual switch, from Stripe's own invoice
 * preview. Every amount is in cents of `currency`.
 */
export class SwitchToAnnualPreviewResponseDto {
  /** What the card is charged today, VAT included (Stripe `amount_due`). */
  amountDueToday!: number;
  /** Today's charge before VAT: annual price minus credit minus discount. */
  amountExcludingTax!: number;
  /** Unused time of the current monthly period, refunded as a credit. */
  credit!: number;
  /** Annual price before discounts and credit. */
  annualPrice!: number;
  /** Discount carried over from the subscription's coupon, 0 when none. */
  discountAmount!: number;
  /** VAT on today's charge, 0 when Stripe computes none. */
  taxAmount!: number;
  /** Invoice total, VAT included, before any customer balance. */
  total!: number;
  /** ISO 4217, lowercase as Stripe returns it (e.g. `eur`). */
  currency!: string;
  /** Unix epoch seconds to send back on confirm. */
  prorationDate!: number;
  /** When the annual subscription renews (ISO 8601). */
  nextRenewalDate!: string;
}

/** Result of the confirmed switch. */
export class SwitchToAnnualResponseDto {
  billingInterval!: 'ANNUAL';
  nextRenewalDate!: string | null;
}
